import { useTenant } from '@emerald-works-nova/auth'
import { ContentViewer } from '@emerald-works-nova/content-viewer'
import { useSession } from '@emerald-works/react-auth'
import { useEvent } from '@emerald-works/react-event-bus-client'
import makeStyles from '@mui/styles/makeStyles'
import clsx from 'clsx'
import dompurify from 'dompurify'
import React, { useRef, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { useSelector, useDispatch } from 'react-redux'
import { GoogleAd } from '../..'
import consts from '../../../consts'
import { useAlert, useTranslation } from '../../../hooks'
import { contentSlice, onboardingSlice, quizResultsSlice } from '../../../reducers'
import { useGleap } from '../../gleap'
import useGoogleAdsense from './../../../hooks/useGoogleAdsense'
import { ALL_NON_PAYING } from '../../../services/cognito/userGroups'
import useGroup from '../../../hooks/useGroup'
import { QuizRecommendationCard } from './quiz-recommendation-card'

// Make the empty body take up space which avoids rendering the recommended
// directly under the article header if they load before the body.
const useStyles = makeStyles(({ breakpoints }) => ({
  empty: {
    minHeight: '100vh'
  },
  loaded: {
    minHeight: 0
  }
}))

const Body = ({ className, body, onAddToPlaylist }) => {
  const classes = useStyles()
  const bodyRef = useRef()
  const { isAuthenticated } = useSession()
  const { isInGroup } = useGroup()
  const isNonPayingUser = isInGroup(ALL_NON_PAYING)
  const tenant = useTenant()
  const isClubTenant = tenant.pk === consts.DEFAULT_CLUB_PK
  const { enqueueMsg } = useAlert()
  const { useAdsense } = useGoogleAdsense()
  const { trackEvent } = useGleap()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const content = useSelector(contentSlice.selectors.selectContent)
  const mcqRecommendations = useSelector(contentSlice.selectors.selectMCQRecommendations)
  const allQuizStates = useSelector(quizResultsSlice.selectors.selectAll)

  // Keep a ref in sync so getQuizState can read the latest saved state without
  // becoming a reactive dependency (avoids cascading re-renders through EditorContext).
  const quizStatesRef = useRef(allQuizStates)
  quizStatesRef.current = allQuizStates

  const getQuizState = useCallback((quizId) => quizStatesRef.current[quizId], [])
  const onQuizStateChange = useCallback((quizId, quizState) => {
    dispatch(quizResultsSlice.actions.saveQuizState({ quizId, quizState }))
  }, [dispatch])
  // Internal links in content (e.g. quiz score feedback) navigate via the SPA
  // router so in-memory quiz state survives — a hard reload would wipe it.
  const onInternalLinkClick = useCallback((url) => navigate(url), [navigate])
  const { quiz: checklistQuizCompleted } = useSelector(onboardingSlice.selectors.selectChecklistItems)
  const { t } = useTranslation('content')

  const translations = useMemo(() => {
    return {
      ewQuiz: {
        statementCountText: t('quiz_statements_to_answer'),
        calculateTotalText: t('quiz_calculate_total'),
        yourScoreText: t('quiz_your_score'),
        mcqYourScore: t('quiz_mcq_your_score'),
        mcqOutOf: t('quiz_mcq_out_of'),
        mcqRecommendations: t('quiz_mcq_recommendations'),
        mcqProgress: t('quiz_mcq_progress'),
        mcqQuestionIndicator: t('quiz_mcq_question_indicator'),
        mcqSelectionHintMultiple: t('quiz_mcq_selection_hint_multiple'),
        mcqSelectionHintSingle: t('quiz_mcq_selection_hint_single'),
        mcqUnansweredTitle: t('quiz_mcq_unanswered_title'),
        mcqGoToQuestion: t('quiz_mcq_go_to_question'),
        mcqQuestionNumber: t('quiz_mcq_question_number'),
        mcqSubmitAnswer: t('quiz_mcq_submit_answer'),
        mcqSubmitQuiz: t('quiz_mcq_submit_quiz'),
        mcqRetake: t('quiz_mcq_retake'),
        mcqShowAnswers: t('quiz_mcq_show_answers'),
        mcqReviewAnswers: t('quiz_mcq_review_answers')
      },
      ewQuestion: {
        submitAnswerText: t('question_submit_answer'),
        correctFeedbackText: t('question_correct_feedback'),
        incorrectFeedbackText: t('question_incorrect_feedback')
      },
      ewElearning: {
        startHereText: t('elearning_start_here'),
        exclusiveToMembersText: t('elearning_exclusive_to_members')
      }
    }
  }, [t])

  const [onboardingItemTrackEvent] = useEvent([
    onboardingSlice.eventBus.onboardingChecklistItemTrackEvent
  ])

  const [getScormLaunchLink] = useEvent([{
    eventName: 'scormGetAccessLink',
    onSuccess: (payload) => {
      window.open(payload.launchLink, '_blank', 'fullscreen=yes, location=yes, scrollbars=yes,status=yes')
    },
    onError: (err) => {
      console.error(err)
      enqueueMsg('An unexpected error happened, please try again later', 'error')
    }
  }])

  const [quizSubmitted] = useEvent([{ eventName: 'quizSubmitted' }])

  const isHTML = body.data?.html?.length && (!body.data?.blocks || !body.data?.entityMap)

  const handleStartScorm = ({ courseId }) => {
    getScormLaunchLink.trigger({ courseId })
  }

  const renderViewer = () => {
    if (isHTML) {
      const sanitizedHtml = dompurify.sanitize(body.data.html, {
        USE_PROFILES: {
          html: true,
          svg: true,
          svgFilters: true
        },
        FORCE_BODY: true,
        ADD_TAGS: ['iframe', 'style'],
        ADD_ATTR: ['target', 'style', 'class']
      })

      return (
        <div
          data-test='article-body'
          className={clsx(className)}
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        />
      )
    }

    return (
      <ContentViewer
        body={body}
        isAuthenticated={isAuthenticated}
        hasScormAccess={!isNonPayingUser}
        scormThumbnailUrl={content?.image?.imgUrl}
        onStartScorm={handleStartScorm}
        isLoadingScormLink={getScormLaunchLink.isWorking}
        isClub={isClubTenant}
        GoogleAdsComponent={({ adKey }) =>
          useAdsense &&
            <GoogleAd
              adKey={adKey}
              adPosition='content-body'
              adSource='components>article>body>index.js'
              fromContent
            />}
        className={clsx(className)}
        mcqRecommendations={mcqRecommendations}
        renderQuizRecommendationCard={(rec) => <QuizRecommendationCard rec={rec} onAddToPlaylist={onAddToPlaylist} />}
        getQuizState={getQuizState}
        onQuizStateChange={onQuizStateChange}
        onInternalLinkClick={onInternalLinkClick}
        onCalculateQuizResult={({ quiz, classification, score }) => {
          console.log('Quiz result on toolkit', quiz, classification, score)
          quizSubmitted.trigger({
            quiz,
            classification,
            score
          })
          trackEvent('Quiz Submitted')
          if (!checklistQuizCompleted) {
            onboardingItemTrackEvent.trigger({
              type: 'checklistQuiz'
            })
          }
        }}
        translations={translations}
        data-test='article-body'
      />
    )
  }

  return (
    <div
      ref={bodyRef}
      data-test='article-body-container'
      className={
        bodyRef.current?.hasChildNodes() ? classes.loaded : classes.empty
      }
    >
      {renderViewer()}
    </div>
  )
}

export default Body
